import { intersectionItems, stringOrArrayToArray } from '../../utils/helper';
import { SCREEN_NAME, SEARCH_TARGET } from '../constants';
import { CountryCode } from '../constants/countries';
import { SearchMapState } from './SearchMapStore.type';
import StoreProvider from './StoreProvider';

export enum ModeViewType {
  'LIST' = 'LIST',
  'MAP' = 'MAP',
}

export enum MapProvider {
  GOOGLE_MAP = 'GOOGLE_MAP',
  OPEN_STREETMAP = 'OPEN_STREETMAP',
}

export interface Modal <T extends {} = {}> {
  id?: string | number
  title?: string;
  component: string;
  className?: string;
  hideDefaultClose?: boolean;
  closeOnClickOutside?: boolean;
  closeOnEscapeKeydown?: boolean;
  componentProps?: T;
  focusOnCloseElement?: HTMLElement;
  focusOnCloseSelector?: string;
}

export interface EntryType {
  screenName?: typeof SCREEN_NAME[keyof typeof SCREEN_NAME];
  specialtyCode?: string[];
  specialtyLabel?: string;
  defaultLocation?: 'nearMe' | {lat: number; lng: number};
}

export type HCODetailDataType = SearchMapState['hcoDetail']
export type HCPDetailDataType = SearchMapState['individualDetail']
export type HCOsDataType = SearchMapState['hcos']
export type HCPsDataType = SearchMapState['specialties']
export type HCLDataScreenType = HCODetailDataType | HCPDetailDataType | HCOsDataType | HCPsDataType
export type HCLEventScreenType = 'HCPDetail' | 'HCODetail' | 'HCOs' | 'HCPs'

export type DistanceUnit = 'km' | 'mi';
export type NestedRecordString = { [k: string]: string | NestedRecordString }
export type ExitLinkPopupConfigType = {
  title?: string | {[k: string]: string};
  description?: string | {[k: string]: string};
  acceptButton?: string | {[k: string]: string};
  acceptButtonStyled: 'primary' | 'primary-reverse' | 'secondary' | 'secondary-reverse' | 'no-styled'
  cancelButton?: string | {[k: string]: string};
  cancelButtonStyled: 'primary' | 'primary-reverse' | 'secondary' | 'secondary-reverse' | 'no-styled'
}

export type HideFieldSearch = 'name' | 'specialty' | 'address' | 'country'


export interface HclSDKConfigData {
  markerIcon?: string;
  markerIconCurrentLocation?: string;
  modeView?: ModeViewType;
  i18nBundlesPath?: string;
  flagBundlesPath?: string;
  i18nCustomObject?: NestedRecordString
  modal?: Modal;
  modals: Array<Modal | undefined>;
  entry?: EntryType;
  icons: any;
  apiKey: string;
  appName?: string;
  appURL?: string;
  locale?: string;
  changeLocaleBySearchCountry?: boolean;
  countryGeo?: string;
  countriesSubscriptionKey?: CountryCode[];
  countriesNarrowList?: Array<string> | string
  distanceUnit?: DistanceUnit;
  distanceDefault?: number;
  showSuggestModification?: boolean;
  enableDarkMode?: boolean;
  enableMapDarkMode?: boolean;
  enableMedicalTerm?: boolean;
  enablePhoneNumberFormat?: boolean;
  disableCollectGeo?: boolean;
  disableAppointment?: boolean;
  stylesheet?: string;
  map: {
    provider: MapProvider;
    googleMapApiKey: string;
    googleMapId: string;
    enableLeafletAttribution?: boolean;
  };
  countryFilterSelected: CountryCode | '';
  enableHcoSearch?: boolean;
  enableUci?: boolean;

  // limit GraphQL query activities to some specific individuals
  individuals?: string[];
  workplaces?: string[];
  // SearchMode
  searchMode?: 'startWithHCP' | 'startWithHCO' | 'onlyHCP' | 'onlyHCO';
  hideSearchBar?: boolean
  disableIndividualsInHcoCard?: boolean
  hideBackHomeButton?: boolean
  hideDistanceData?: boolean
  hideFeedbackInfo?: boolean,
  hideTotalResult?: boolean,
  hideSortPanel?: boolean,
  hideBackToSearch?: boolean
  hideShareButton?: boolean
  hidePhoneUtilIcon?: boolean
  hideSpecialtySearch?: boolean
  hideSearchField?: {
    name?: boolean | 'onlyHCO' | 'onlyHCP' | 'all',
    specialty?: boolean | 'onlyHCO' | 'onlyHCP' | 'all',
    address?: boolean | 'onlyHCO' | 'onlyHCP' | 'all',
    country?: boolean | 'onlyHCO' | 'onlyHCP' | 'all',
  }
  hideLocationSearch?: boolean
  hideHomeIntro?: boolean
  disableMapListMode?: boolean
  sortWorkplacesByItsInputOrder?: boolean
  hideHCOTypeLabel?: boolean
  exitPopupExternalLink?: boolean | ExitLinkPopupConfigType
  // function settings to be triggered every time data on screen change
  onHCLData?: (data: { type: HCLEventScreenType, data: HCLDataScreenType | null }) => void, 
  // function settings to be triggered every time data on screen HCO detail show or leave
  onHCODetail?: (data: HCODetailDataType | null) => void
  // function settings to be triggered every time data on screen HCP detail show or leave
  onHCPDetail?: (data: HCPDetailDataType | null) => void
  // function settings to be triggered every time data on screen list HCO suggesstions show or leave
  onHCOs?: (data: HCOsDataType | null) => void
  // function settings to be triggered every time data on screen list HCP suggesstions show or leave
  onHCPs?: (data: HCPsDataType | null) => void
  // function settings to be triggered every time country search changed
  onCountryChange?: (data: {code: string, label: string}) => void
  // function settings to be triggered every time locale language changed. This settings only works when settings changeLocaleBySearchCountry is set
  onLocaleChange?: (data: string) => void
}

export const initStateConfigStore: HclSDKConfigData = {
  apiKey: '',
  map: {
    provider: MapProvider.OPEN_STREETMAP,
    googleMapApiKey: '',
    googleMapId: '',
    enableLeafletAttribution: false,
  },
  modeView: ModeViewType.LIST,
  entry: {},
  modal: undefined,
  modals: [],
  icons: {},
  appName: '',
  appURL: '',
  countryGeo: '', // From Geolocation
  countriesSubscriptionKey: [], // From Subscription Key
  countriesNarrowList: [], // From config
  distanceUnit: 'km',
  distanceDefault: 0,
  enableDarkMode: false,
  enableMapDarkMode: false,
  enableMedicalTerm: false,
  disableCollectGeo: false,
  disableAppointment: false,
  hideSearchField: {},
  showSuggestModification: true,
  disableMapListMode: false,
  countryFilterSelected: '',
  i18nBundlesPath: process.env.DEFAULT_I18N_BUNDLE_PATH,
  flagBundlesPath: process.env.DEFAULT_I18N_BUNDLE_PATH?.replace('i18n', 'assets'),
  i18nCustomObject: {},

  // hco search config
  enableHcoSearch: false,
  // uci
  enableUci: true,
  searchMode: undefined,
  hideDistanceData: false,
};

class ConfigStore extends StoreProvider<HclSDKConfigData> {

  setState = (states: Partial<HclSDKConfigData>) => {
    if (states) {
      const statesKeys = Object.keys(states)
      statesKeys.forEach((k) => {
        if (k === 'modeView' && !!this.state.disableMapListMode) {
          if (this.state.modeView === ModeViewType.MAP) {
            this.storeInstance.set('modeView', ModeViewType.LIST)
          }
          return
        }
        this.storeInstance.set(k as any, (states as any)[k])
      })
    }
  }
  addModal = (modal: Modal) => {
    if (modal) {
      this.storeInstance.set('modals', [...this.state.modals, modal])
    }
  }

  get configGraphql() {
    return {
      headers: {
        'Ocp-Apim-Subscription-Key': this.state.apiKey,
      },
    };
  }

  get countryGraphqlQuery() {
    const { countryGeo, countryFilterSelected } = this.state;
    const countryGeoFormated = countryGeo.toUpperCase() as CountryCode;
    const countriesAvalable = this.countriesAvailable

    if (countryFilterSelected) {
      return countryFilterSelected;
    }

    if (countriesAvalable.includes('UK') && countryGeoFormated === 'GB') {
      return 'UK';
    }

    if (countriesAvalable.includes('BK') && countryGeoFormated === 'HR') {
      return 'BK';
    }

    if (countriesAvalable.includes(countryGeoFormated)) {
      return countryGeoFormated;
    }

    return countriesAvalable[0];
  }

  get isHCOAvailable() {
    if (typeof this.state.searchMode === 'undefined' || !this.state.searchMode) {
      return this.state.enableHcoSearch;
    }
    return this.state.searchMode !== 'onlyHCP';
  }

  get isHCPAvailable() {
    return this.state.searchMode !== 'onlyHCO';
  }

  get initialSearchMode() {
    switch (this.state.searchMode) {
      case 'startWithHCO':
      case 'onlyHCO':
        return SEARCH_TARGET.HCO;
      case 'startWithHCP':
      case 'onlyHCP':
      default:
        return SEARCH_TARGET.HCP;
    }
  }

  get isDisableMapListMode() {
    return this.state.disableMapListMode
  }

  get entryConfig() {
    return this.state.entry;
  }
  get isHideSearchBar() {
    return this.state.hideSearchBar
  }
  get isSortWorkplaceByItsOrder() {
    return Array.isArray(this.state.workplaces) && this.state.workplaces.length > 0 && this.state.sortWorkplacesByItsInputOrder
  }

  get countriesAvailable(): CountryCode[] {
    const { countriesSubscriptionKey, countriesNarrowList } = this.state;
    const narrowedCountryArr = stringOrArrayToArray(countriesNarrowList || [])
  
    return narrowedCountryArr.length > 0 ? 
      intersectionItems(
        countriesSubscriptionKey, 
        narrowedCountryArr).map((item) => item.toUpperCase()
      ) : countriesSubscriptionKey
  }

  get isGeoCountryAvailable() {
    return this.countriesAvailable.includes((this.state.countryGeo.toUpperCase() || '') as CountryCode)
  }

  get isExitLinkPopup() {
    return Boolean(this.state.exitPopupExternalLink)
  }

  static getSearchModeByConfig(config: Partial<HclSDKConfigData>) {
    switch (config.searchMode) {
      case 'startWithHCO':
      case 'onlyHCO':
        return SEARCH_TARGET.HCO;
      case 'startWithHCP':
      case 'onlyHCP':
      default:
        return SEARCH_TARGET.HCP;
    }
  }
}

export default ConfigStore;
